/**
 * Who is offered in every "pick a trader" box: one question, every page.
 *
 * The bunkers enquiry and the bunkers recap each asked the server for their
 * own trader list. The recap was fixed to pull every page (a03495e8, after it
 * was found stopping at 100 with no way to reach trader 101); the enquiry kept
 * asking for one page of 100 (ledger row 133). Both boxes are plain <select>s
 * with no search and no next button, so anyone past the first page could not
 * be picked at all and nothing on screen said so.
 *
 * Every trader box now loads its people here, so the two screens cannot ask
 * different questions again. Name them with traderLabel (./traderLabel.js).
 *
 * @returns {Promise<{rows, total, truncated}>} see fetchEveryPage.
 */
import axiosInstance from './axiosConfig';
import fetchEveryPage, { normalizePage } from './fetchEveryPage';

export const fetchTraderList = () => fetchEveryPage(async (page) => {
  const response = await axiosInstance.get('/api/hms-employees/', {
    params: {
      position: 'trader', active: 'true', page, page_size: 100,
    },
  });
  return normalizePage(response.data);
});

const BOXES = ['customer', 'supplier'];

/**
 * Who a bunkers trader box offers (ledger row 134, HMS: "remove the coal
 * traders from the dropdowns").
 *
 *  - Only people ticked **Liquid Fuels** on their staff profile (HMS Admin, edit
 *    employee). Coal traders are ticked Solid Fuels; someone in both keeps both
 *    ticks and stays. On live (2026-09-21) this kept 22 of 33 and dropped the 2
 *    coal traders, 8 accounts with no bunkers access, and one bunkers Supply
 *    Trader whose tick was missing.
 *  - A **supplier** box also leaves out restricted customer traders: they only
 *    ever see deals where they are the Customer Trader, so they could never
 *    work the supply side of a deal they were picked for.
 *  - The deal's own **saved** trader always stays, even when the rule would
 *    leave them out (a coal trader on an old deal, a title without "trader",
 *    someone who left), so the box names who is on the deal instead of going
 *    empty. `saved` is the deal's `*_trader_detail` from the server.
 *
 * The server sends the facts (employees/trader_rows.py); a missing fact counts
 * as "no", never as "yes".
 */
export const bunkersTraderOptions = (traders, { box, saved } = {}) => {
  if (!BOXES.includes(box)) {
    throw new Error(`bunkersTraderOptions: unknown box "${box}"`);
  }
  const offered = (traders || []).filter((t) => t.business_liquid_fuels === true
    && !(box === 'supplier' && t.restricted_customer_trader === true));
  if (saved && saved.id != null && !offered.some((t) => String(t.id) === String(saved.id))) {
    return [...offered, saved];
  }
  return offered;
};

/** Shown under a trader box that has nobody to offer, instead of a silent empty list. */
export const NO_BUNKERS_TRADERS = 'Nobody here is ticked Liquid Fuels on their staff profile, '
  + 'so there is no bunkers trader to pick. An admin sets it in IMT Admin.';

/** The note for a box once the list has loaded, or undefined when people are offered. */
export const traderBoxNote = (traders, box) => (
  bunkersTraderOptions(traders, { box }).length === 0 ? NO_BUNKERS_TRADERS : undefined
);

export default fetchTraderList;
